import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { Fragment, useEffect, useRef, useState } from 'react'
import { Filter, Search, X } from 'react-feather'
import { Trans } from '@lingui/macro'
import I18nNextSeo from '@components/common/i18n/i18n-next-seo'
import NavBar from '@components/common/navbar'
import Footer from '@components/common/footer'
import Input from '@components/common/forms/input'
import CourseCard, { Course } from '@components/courses/course-card'
import CourseFilterChip from '@components/courses/course-filter-chip'
import CourseFiltersModal from '@components/courses/course-filters-modal'
import VideoModal from '@components/courses/video-modal'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import CodingalSubscription from '@components/home/codingal-subscription'
import CoursesFAQ from '@components/courses/courses-faq'
import PersonalizedTrialStrip from '@components/common/personalized-trial-strip'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { COURSES_FAQ_SECTIONS } from '@lib/data/courses-faq'
import { CClient } from '@common/utils'
import useResolution from '@hooks/useResolution'
import * as Sentry from '@sentry/node'
import { coursesPageSeo } from '@lib/seo'
import Breadcrumb from '@components/common/breadcrumb'
import { COURSES_ITEMS } from '@lib/data/breadcrumb'
import { FAQPageJsonLd } from 'next-seo'
import CourseCollectionsJsonLd from '@components/courses/course-collections-json-ld'
import {
  CourseGroup,
  groupCampCoursesBySubject,
  groupCoursesBySubject,
  isCampCourse,
} from '@lib/utils/course-categorization'
import {
  applyFiltersToGroups,
  buildQueryWithCourseFilters,
  CourseFilterKey,
  CourseFilterState,
  EMPTY_COURSE_FILTERS,
  FilterOption,
  getAgeOptions,
  getCourseTypeOptions,
  getSubjectOptions,
  hasActiveCourseFilters,
  parseCourseFiltersFromQuery,
} from '@lib/utils/course-filters'

const renderGroupTitle = (group: CourseGroup) => {
  if (group.titleId) return <Trans id={group.titleId} />
  return group.title
}

type CourseFilterDefinition = {
  key: CourseFilterKey
  label: string
  options: FilterOption[]
  twoColumn?: boolean
}

// Filters shown inline on mobile; the "Filters" icon opens a master-detail
// panel with every filter. On desktop every filter renders inline instead.

const getFilterDefinitions = (courses: Course[]): CourseFilterDefinition[] => [
  {
    key: 'age',
    label: 'Age',
    options: getAgeOptions(courses),
    twoColumn: true,
  },
  { key: 'subject', label: 'Subject', options: getSubjectOptions(courses) },
  {
    key: 'courseType',
    label: 'Course type',
    options: getCourseTypeOptions(courses),
  },
]

type CoursesPageProps = {
  initialCourses: Course[]
}

const CoursesPage = ({ initialCourses }: CoursesPageProps) => {
  const router = useRouter()
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [
    activeFilterKey,
    setActiveFilterKey,
  ] = useState<CourseFilterKey | null>(null)
  const [selectedVideo, setSelectedVideo] = useState<{
    url: string
    title: string
  } | null>(null)
  // Filters live in the URL query so a filtered courses page can be shared via
  // its link. The URL is the source of truth: `filters` is derived from
  // `router.query` and every change is written back with a shallow route
  // replace (shallow = no re-run of getServerSideProps, no extra data fetch).
  const filters = parseCourseFiltersFromQuery(router.query, initialCourses)
  // Search filters the list on every keystroke from local state; the URL `q`
  // param is written on a debounce since Safari throws past 100 history
  // replaces per 30 seconds.
  const [search, setSearch] = useState(
    typeof router.query.q === 'string' ? router.query.q : ''
  )

  useEffect(() => {
    const query = search.trim()
    if (query === (router.query.q ?? '')) return
    const timeout = setTimeout(() => {
      const nextQuery = { ...router.query }
      if (query) nextQuery.q = query
      else delete nextQuery.q
      router.replace(
        { pathname: router.pathname, query: nextQuery },
        undefined,
        { shallow: true, scroll: false }
      )
    }, 300)
    return () => clearTimeout(timeout)
  }, [search, router])

  const applyFilters = (
    updater: (prev: CourseFilterState) => CourseFilterState
  ) => {
    const nextFilters = updater(filters)
    router.replace(
      {
        pathname: router.pathname,
        query: buildQueryWithCourseFilters(router.query, nextFilters),
      },
      undefined,
      { shallow: true, scroll: false }
    )
  }

  // Collapsing the chip fold while the bar is stuck is a mobile-only space
  // saving measure — desktop/tablet has room to keep showing active filters.
  const { sizes } = useResolution()
  const isDesktopOrTabletViewport = sizes.lg || sizes.xl || sizes['2xl']

  // Detects whether the filter bar has actually reached its sticky (`top:
  // 4rem`) pinned position, vs. still sitting in its normal place further
  // down the page. While pinned, the applied-filter chip list is capped to a
  // fixed, scrollable height instead of growing to its full natural height,
  // which otherwise ate most of the mobile viewport (see screenshot in
  // CE-8093 review).
  const filterBarStickyOffsetPx = 64 // matches the `top: '4rem'` below
  const filterBarSentinelRef = useRef<HTMLDivElement>(null)
  const [isFilterBarStuck, setIsFilterBarStuck] = useState(false)
  // Measures the chip list's own natural height (same technique as
  // CourseDescription's read-more fold) so the collapse/expand transition
  // animates between real values instead of an arbitrary placeholder.
  const activeFilterChipsRef = useRef<HTMLDivElement>(null)
  // Toggling isFilterBarStuck resizes the chip fold, which resizes the
  // sticky bar's own footprint while it's pinned — that layout change can
  // itself re-trigger the sentinel's intersection callback, flipping the
  // state right back and looping indefinitely while the page is scrolling.
  // Locking out further toggles until the resize (CSS transition) has fully
  // settled breaks that feedback loop.
  const isFilterBarTransitionLockedRef = useRef(false)

  useEffect(() => {
    const sentinel = filterBarSentinelRef.current
    if (!sentinel || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isFilterBarTransitionLockedRef.current) return
        const nextStuck = !entry.isIntersecting
        setIsFilterBarStuck((current) => {
          if (current === nextStuck) return current
          isFilterBarTransitionLockedRef.current = true
          setTimeout(() => {
            isFilterBarTransitionLockedRef.current = false
          }, 400)
          return nextStuck
        })
      },
      { rootMargin: `-${filterBarStickyOffsetPx}px 0px 0px 0px`, threshold: 0 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  const toggleFilterValue = (key: CourseFilterKey, value: string) => {
    applyFilters((prev) => {
      const current = prev[key]
      const next = current.includes(value)
        ? current.filter((selected) => selected !== value)
        : [...current, value]
      return { ...prev, [key]: next }
    })
  }

  const resetFilters = () => applyFilters(() => EMPTY_COURSE_FILTERS)

  const openFilterModalForKey = (key: CourseFilterKey) => {
    setActiveFilterKey(key)
    setIsFilterModalOpen(true)
  }

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing')
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const handleVideoPlay = (course: Course) => {
    if (course.introVideoUrl) {
      setSelectedVideo({
        url: course.introVideoUrl,
        title: course.title,
      })
      setIsVideoModalOpen(true)
    }
  }

  const campCourses = initialCourses.filter(isCampCourse)
  const subjectGroups = groupCoursesBySubject(
    initialCourses.filter((course) => !isCampCourse(course))
  )
  const campGroups = groupCampCoursesBySubject(campCourses)
  const courseGroups: CourseGroup[] = [...subjectGroups, ...campGroups]

  const filterDefinitions = getFilterDefinitions(initialCourses)
  const filteredGroups = applyFiltersToGroups(courseGroups, filters, search)
  const isFiltered = hasActiveCourseFilters(filters)

  const totalActiveCount = filterDefinitions.reduce(
    (sum, filter) => sum + filters[filter.key].length,
    0
  )

  const activeFilterChips = filterDefinitions.flatMap((filter) =>
    filters[filter.key].map((value) => ({
      key: filter.key,
      value,
      label:
        filter.options.find((option) => option.value === value)?.label ?? value,
    }))
  )

  const renderGroup = (group: CourseGroup) => (
    <div
      className="container mx-auto flex flex-col space-y-10 leading-tight pb-8 pt-12 text-grey px-5"
      key={group.key}
    >
      <h2 className="text-2xl lg:text-4xl font-600 text-center">
        {renderGroupTitle(group)}
      </h2>
      <div className="space-y-6">
        {group.courses.map((course) => (
          <CourseCard
            key={course.id}
            course={course}
            onViewPricing={scrollToPricing}
            onVideoPlay={handleVideoPlay}
            isCampCourse={isCampCourse(course)}
            titleAs="h3"
          />
        ))}
      </div>
    </div>
  )

  return (
    <>
      <I18nNextSeo {...coursesPageSeo} />
      <FAQPageJsonLd
        mainEntity={COURSES_FAQ_SECTIONS.flatMap(({ items }) =>
          items.map(({ question, answerText }) => ({
            acceptedAnswerText: answerText,
            questionName: question,
          }))
        )}
      />
      <CourseCollectionsJsonLd
        groups={courseGroups}
        pageName="AI, Coding, Math & Public Speaking Courses for Kids & Teens"
        pageUrl="https://www.codingal.com/courses/"
      />
      <NavBar isSticky />
      <main
        className="text-grey"
        style={{ maxWidth: '70rem', margin: '0 auto' }}
      >
        {/* Hero Section */}
        <section className="bg-white pt-12 px-5 text-center">
          <div>
            <div className="self-start pb-6">
              <Breadcrumb isInCoursePage items={COURSES_ITEMS} />
            </div>
            <h1 className="text-center font-600 custom-home-h1">
              <span className="yellow-underline">
                AI, Coding, Math & Public Speaking
              </span>{' '}
              Courses for Kids & Teens
            </h1>
            <p className="font-400 text-lg md:text-xl text-center my-3 md:my-0 md:mt-5">
              Browse our diverse range of online Codingal courses designed to
              spark creativity, build essential skills, and prepare your child
              for the future—all at their own pace.
            </p>
          </div>
        </section>

        {/* Courses Section */}
        <section className="bg-white py-8 lg:py-12">
          <div>
            {/* Empty State */}
            {initialCourses.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 space-y-4">
                <p className="text-2xl font-600 text-center text-grey-700">
                  No courses found
                </p>
                <p className="text-grey-700 text-center">
                  Check back soon for new courses.
                </p>
              </div>
            ) : (
              <>
                <div ref={filterBarSentinelRef} style={{ height: 1 }} />
                <div
                  className="sticky z-20 container mx-auto bg-white border-b border-grey-300 py-2 px-5 flex flex-wrap items-center justify-between gap-2 md:gap-6"
                  // The chip fold below resizes while this bar is pinned at
                  // the top of the viewport. Without disabling scroll
                  // anchoring, the browser compensates for that resize by
                  // nudging scrollTop, which shifts the sentinel across the
                  // stuck/unstuck threshold and re-triggers the resize —
                  // an infinite expand/collapse loop while scrolling.
                  style={{ top: '4rem', overflowAnchor: 'none' }}
                >
                  {/* The border sits on this wrapper so it surrounds both the
                      icon and the field, like a single search box. A label
                      so a click anywhere inside it focuses the field. */}
                  <label
                    htmlFor="course-search"
                    className="flex flex-1 items-center cursor-text max-w-sm px-3 rounded-md border border-grey-300 course-search"
                  >
                    <Search
                      size={16}
                      aria-hidden="true"
                      className="text-grey-700 mr-2 flex-shrink-0"
                    />
                    <Input
                      className="border-0 px-0 py-1 sm:py-2 focus:shadow-none"
                      wrapperClassName="flex-1"
                      id="course-search"
                      name="course-search"
                      type="search"
                      placeholder="Search courses"
                      autoComplete="off"
                      value={search}
                      onChange={(event) => setSearch(event.currentTarget.value)}
                    />
                  </label>
                  <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2">
                    {/* Both breakpoints: "Filters" opens every filter in one place */}
                    {filterDefinitions.some(
                      (filter) => filter.options.length > 0
                    ) && (
                      <button
                        type="button"
                        onClick={() => setIsFilterModalOpen(true)}
                        aria-label="Filter by"
                        className="inline-flex items-center gap-2 pr-2 pl-1 py-1 focus:outline-none transition-colors"
                      >
                        <span className="relative inline-flex">
                          <Filter size={18} className="text-orange" />
                          {totalActiveCount > 0 && (
                            <span
                              className="absolute inline-flex items-center justify-center rounded-full bg-orange text-white leading-none h-4 w-4 text-xs font-600"
                              style={{ top: '-8px', right: '-8px' }}
                            >
                              {totalActiveCount}
                            </span>
                          )}
                        </span>
                        <span className="text-sm font-500 text-grey-900">
                          Filter by
                        </span>
                      </button>
                    )}

                    {/* Hidden on phones so search and "Filter by" fit on one
                        row; the filter panel covers every filter there. */}
                    <div className="hidden sm:flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
                      {filterDefinitions.map((filter) =>
                        filter.options.length > 0 ? (
                          <CourseFilterChip
                            key={filter.key}
                            label={filter.label}
                            count={filters[filter.key].length}
                            onClick={() => openFilterModalForKey(filter.key)}
                          />
                        ) : null
                      )}
                    </div>

                    {/* Hidden on phones for room; each applied chip has its own
                        remove button and the filter panel has "Clear all". */}
                    {isFiltered && (
                      <button
                        type="button"
                        onClick={resetFilters}
                        className="hidden sm:inline text-orange font-600 text-xs focus:outline-none hover:underline px-1"
                      >
                        <Trans>Reset filters</Trans>
                      </button>
                    )}
                  </div>

                  {/* Applied filters: each selected value, removable
                      individually. Same fold technique as CourseDescription's
                      read-more: an outer overflow-hidden/transition wrapper
                      whose `maxHeight` is driven by the inner content's own
                      measured `clientHeight`. On mobile it collapses to 0
                      once the bar is actually stuck (to save space); on
                      desktop/tablet it always stays at its natural height. */}
                  {activeFilterChips.length > 0 && (
                    <div
                      className="w-full overflow-hidden transition-all duration-300"
                      style={{
                        maxHeight:
                          isFilterBarStuck && !isDesktopOrTabletViewport
                            ? '0px'
                            : activeFilterChipsRef.current
                            ? `${
                                activeFilterChipsRef.current.clientHeight + 8
                              }px`
                            : 'none',
                        overflowAnchor: 'none',
                      }}
                    >
                      <div
                        ref={activeFilterChipsRef}
                        className="flex flex-wrap items-center justify-center gap-1 mt-2"
                      >
                        {activeFilterChips.map((chip, index) => (
                          <Fragment key={`${chip.key}-${chip.value}`}>
                            {index > 0 &&
                              chip.key !== activeFilterChips[index - 1].key && (
                                <span
                                  aria-hidden="true"
                                  className="w-px h-4 bg-grey-500"
                                />
                              )}
                            <span className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-grey-100 text-grey-900 text-xs">
                              {chip.label}
                              <button
                                type="button"
                                onClick={() =>
                                  toggleFilterValue(chip.key, chip.value)
                                }
                                aria-label={`Remove ${chip.label} filter`}
                                className="focus:outline-none hover:text-orange"
                              >
                                <X size={12} />
                              </button>
                            </span>
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <CourseFiltersModal
                  isOpen={isFilterModalOpen}
                  setIsOpen={setIsFilterModalOpen}
                  filters={filterDefinitions}
                  selectedValues={filters}
                  activeKey={activeFilterKey}
                  onActiveKeyChange={setActiveFilterKey}
                  onToggle={toggleFilterValue}
                  onClearAll={resetFilters}
                />
                {filteredGroups.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 space-y-4">
                    <p className="text-2xl font-600 text-center text-grey-700">
                      <Trans>No courses match your search</Trans>
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSearch('')
                        resetFilters()
                      }}
                      className="text-orange font-600 focus:outline-none hover:underline"
                    >
                      <Trans>Clear search and filters</Trans>
                    </button>
                  </div>
                ) : (
                  <div>{filteredGroups.map(renderGroup)}</div>
                )}
              </>
            )}
          </div>
        </section>

        <PersonalizedTrialStrip />

        {/* Video Testimonials Section */}
        <section className="bg-white">
          <VideoTestimonial
            title="Students and parents love Codingal's training program and curriculum"
            video_testimonial={VIDEO_TESTIMONIAL}
            subject="coding"
          />
        </section>

        {/* Trustpilot Reviews with CTAs */}
        <TrustpilotReview shouldScrollToTop={false} />

        {/* Pricing Plans Section */}
        <section id="pricing">
          <CodingalSubscription subject="Coding" />
        </section>

        {/* FAQ Section */}
        <CoursesFAQ sections={COURSES_FAQ_SECTIONS} />
      </main>
      <Footer />

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoModalOpen}
        setIsOpen={setIsVideoModalOpen}
        videoUrl={selectedVideo?.url || null}
        title={selectedVideo?.title || ''}
      />

      <style jsx>{`
        .course-search:focus-within {
          border-color: #ff5a43;
        }
      `}</style>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<CoursesPageProps> = async () => {
  try {
    let allCourses: Course[] = []
    let endpoint = '/api/v1/courses/?page_size=100'

    // Fetch all pages
    while (endpoint) {
      const response = await CClient(endpoint)

      if (!response.ok) {
        throw new Error(`Failed to fetch courses: ${response.status}`)
      }

      const data = await response.json()
      const courses = data.results || data
      allCourses = [...allCourses, ...courses]

      // Check if there's a next page
      if (data.next) {
        try {
          const url = new URL(data.next)
          endpoint = `${url.pathname}${url.search}`
        } catch {
          endpoint = data.next
        }
      } else {
        endpoint = null
      }
    }

    return {
      props: {
        initialCourses: allCourses,
      },
    }
  } catch (error) {
    console.error('Error fetching courses:', error)
    Sentry.captureException(error)

    // Return empty state on error
    return {
      props: {
        initialCourses: [],
      },
    }
  }
}

export default CoursesPage
